import { useMemo } from 'react';

import {
  GET_SCREENER_DATA,
  MapCoordsGranularity,
  type ScreenerReq,
} from '@revelio/data-access';

import { useEffectiveFilters } from '../../filters/hooks/useEffectiveFilters';
import { FilterId, type PrimaryFilterId } from '../../filters/types/filters';
import { buildScreenerFilters } from '../../filters/utils/get-screener-filters';
import { useChart } from '../Chart/ChartProvider';
import type { MapPoint } from '../Chart/Map/types';
import { determineLocationType } from '../Chart/Map/utils/location-type-utils';
import type { ScreenerChartType } from '../PageConfigProvider/types/chart';
import type { ScreenerMetricValues } from '../PageConfigProvider/types/metric';
import { type DataProviderChildren, DataType, type MapDataSet } from './types';
import {
  useCoordinateResolverForMixedGranularities,
  withCoordinateStatus,
} from './utils/coordinate-resolver';
import { useUrqlQueryWithStatus } from './utils/data-provider-results-utils';
import {
  mapMetricToField,
  type ScreenerMetricField,
} from './utils/screener-metrics-utils';

export const ScreenerMapDataProvider = ({
  primaryDimension,
  metric,
  chartType,
  children,
}: {
  primaryDimension: PrimaryFilterId;
  metric: ScreenerMetricValues;
  chartType: ScreenerChartType;
  children: DataProviderChildren<MapDataSet | undefined>;
}) => {
  const { item: _item } = useChart();
  const effectiveFilters = useEffectiveFilters();

  const screenerFilters = buildScreenerFilters({
    effectiveFilters,
    primaryDimension,
    metric,
    pagination: { limit: 1000, offset: 0 },
  });

  const isGeographyView = primaryDimension === FilterId.GEOGRAPHY;

  return (
    <ScreenerQuery
      filters={screenerFilters}
      isGeographyView={isGeographyView}
      metric={metric}
      chartType={chartType}
    >
      {children}
    </ScreenerQuery>
  );
};

const ScreenerQuery = ({
  filters,
  isGeographyView,
  metric,
  chartType,
  children,
}: {
  filters: ScreenerReq;
  isGeographyView: boolean;
  metric: ScreenerMetricValues;
  chartType: ScreenerChartType;
  children: DataProviderChildren<MapDataSet | undefined>;
}) => {
  const [{ data, status }] = useUrqlQueryWithStatus({
    query: GET_SCREENER_DATA,
    variables: { filters },
    pause: !isGeographyView,
  });

  const entities = useMemo(
    () => data?.screener?.entities ?? [],
    [data?.screener?.entities]
  );

  const granularitiesWithIds = entities.map((entity) => ({
    granularity: MapCoordsGranularity.Msa,
    ids: [entity.entity_id],
  }));

  const { data: coords, ...coordsState } =
    useCoordinateResolverForMixedGranularities(granularitiesWithIds);

  const mapPoints: MapPoint[] = useMemo(() => {
    if (chartType !== 'map' || !coords.length || !entities.length) {
      return [];
    }

    const coordMap = new Map(coords.map((c) => [c.id, c] as const));
    return entities
      .map((entity) => {
        const coord = coordMap.get(Number(entity.entity_id));
        if (!coord) return null;

        const field = mapMetricToField(metric);
        const rawValue = (entity as Record<ScreenerMetricField, number[]>)[
          field
        ];
        const original = Array.isArray(rawValue)
          ? rawValue[rawValue.length - 1]
          : rawValue;
        const label = entity.long_name || entity.short_name;
        const locationType = determineLocationType(
          label,
          coord.granularity || '',
          coord.name
        );
        const value = Number(original);
        if (value === undefined || value === null) return null;

        return {
          id: String(entity.entity_id),
          name: label,
          label,
          lat: coord.lat,
          lon: coord.lon,
          value,
          locationType,
        };
      })
      .filter(
        (point): point is NonNullable<typeof point> => point !== null
      ) as MapPoint[];
  }, [chartType, coords, entities, metric]);

  return children({
    data: { type: DataType.MAP, points: mapPoints },
    ...withCoordinateStatus(status, coordsState),
  });
};
